/**
 * EmailSignups Admin Page
 *
 * Shows all email addresses collected from the homepage signup widget.
 * Tammy can see who signed up and export the list as CSV.
 */

import { useState, useEffect } from 'react';
import { HiMail, HiDownload, HiRefresh } from 'react-icons/hi';
import { adminFetch } from '../lib/adminApi';

const API_URL = import.meta.env.VITE_API_URL || '';

interface Signup {
  id:         string;
  email:      string;
  source:     string;
  created_at: string;
}

export default function EmailSignups() {
  const [signups,  setSignups]  = useState<Signup[]>([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState<string | null>(null);

  // ── Fetch signups from backend ────────────────────────────────────────────
  const fetchSignups = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminFetch(`${API_URL}/api/email-signups`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load signups');
      setSignups(data.signups || []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSignups(); }, []);

  // ── CSV export ─────────────────────────────────────────────────────────────
  const exportCsv = () => {
    const header = 'Email,Source,Signed Up';
    const rows   = signups.map(s =>
      `"${s.email}","${s.source}","${new Date(s.created_at).toLocaleString()}"`
    );
    const blob = new Blob([[header, ...rows].join('\n')], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `email-signups-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 rounded-xl">
            <HiMail className="w-6 h-6 text-purple-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Email List</h1>
            <p className="text-sm text-gray-500">
              Visitors who signed up for weekly specials via the homepage.
            </p>
          </div>
        </div>

        <div className="flex gap-2">
          <button
            onClick={fetchSignups}
            className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-xl text-sm hover:bg-gray-50"
          >
            <HiRefresh className="w-4 h-4" />
            Refresh
          </button>
          {signups.length > 0 && (
            <button
              onClick={exportCsv}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-xl text-sm hover:bg-purple-700"
            >
              <HiDownload className="w-4 h-4" />
              Export CSV
            </button>
          )}
        </div>
      </div>

      {/* Stats bar */}
      <div className="bg-purple-50 rounded-2xl px-5 py-4 mb-6 flex items-center gap-4">
        <span className="text-3xl font-bold text-purple-700">{signups.length}</span>
        <span className="text-purple-600 text-sm">
          {signups.length === 1 ? 'subscriber' : 'subscribers'} total
        </span>
      </div>

      {/* Loading */}
      {loading && (
        <div className="text-center py-16 text-gray-400">Loading signups…</div>
      )}

      {/* Error */}
      {!loading && error && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-5 py-4 text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && signups.length === 0 && (
        <div className="text-center py-16">
          <HiMail className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">No signups yet</p>
          <p className="text-gray-400 text-sm mt-1">
            Once visitors sign up on the homepage, they'll appear here.
          </p>
        </div>
      )}

      {/* Signups table */}
      {!loading && !error && signups.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
              <tr>
                <th className="px-5 py-3 text-left font-semibold">#</th>
                <th className="px-5 py-3 text-left font-semibold">Email</th>
                <th className="px-5 py-3 text-left font-semibold">Source</th>
                <th className="px-5 py-3 text-left font-semibold">Signed Up</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {signups.map((s, i) => (
                <tr key={s.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-5 py-3 text-gray-400">{i + 1}</td>
                  <td className="px-5 py-3 font-medium text-gray-900">{s.email}</td>
                  <td className="px-5 py-3">
                    <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                      {s.source}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-gray-500">
                    {new Date(s.created_at).toLocaleDateString('en-CA', {
                      year:  'numeric',
                      month: 'short',
                      day:   'numeric',
                    })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
